import { useEffect, useRef, useState, type ReactNode } from "react";
import { Skeleton } from "./Skeleton";
import s from "./pulltorefresh.module.css";

export const PULL_THRESHOLD = 64;
const MAX_PULL = 96;

export interface PullToRefreshProps {
  onRefresh: () => Promise<unknown> | void;
  /** Off where pulling would fight another gesture (an open sheet, a thread). */
  disabled?: boolean;
  /** Scroll offset of whatever scrolls; default is the page. Pulling only starts at the top. */
  getScrollTop?: () => number;
  children: ReactNode;
}

/** Pull down at the top of the page; past 64px, `onRefresh` runs and a skeleton bar stands in until it settles. Never a spinner. */
export function PullToRefresh({ onRefresh, disabled, getScrollTop = () => window.scrollY, children }: PullToRefreshProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const cb = useRef({ onRefresh, disabled, getScrollTop });
  cb.current = { onRefresh, disabled, getScrollTop };
  const busyRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let y0: number | null = null;
    let dist = 0;
    const start = (e: TouchEvent) => {
      const t = e.touches[0];
      y0 = !cb.current.disabled && !busyRef.current && cb.current.getScrollTop() <= 0 && t ? t.clientY : null;
      dist = 0;
    };
    const move = (e: TouchEvent) => {
      const t = e.touches[0];
      if (y0 === null || !t) return;
      dist = t.clientY - y0;
      if (dist <= 0) { y0 = dist < -4 ? null : y0; setPull(0); return; }
      if (e.cancelable) e.preventDefault();
      setPull(Math.min(MAX_PULL, dist * 0.6));
    };
    const end = async () => {
      const d = dist;
      y0 = null;
      dist = 0;
      setPull(0);
      if (d < PULL_THRESHOLD) return;
      busyRef.current = true;
      setBusy(true);
      try { await cb.current.onRefresh(); } finally { busyRef.current = false; setBusy(false); }
    };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", end);
    el.addEventListener("touchcancel", end);
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", end);
    };
  }, []);

  return (
    <div ref={ref} className={s.ptr} data-pulling={pull > 0 || undefined} data-refreshing={busy || undefined}>
      <div className={s.slot} style={{ height: busy ? 28 : pull }} aria-hidden={busy ? undefined : true}>
        {busy || pull > 0 ? <Skeleton height={6} width={96} style={{ borderRadius: 3 }} /> : null}
      </div>
      {busy ? <span className="sr-only" role="status">Refreshing</span> : null}
      {children}
    </div>
  );
}
